import { makeStyles } from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { statusBackgroundColor } from './utils';

import DefaultView from './views/DefaultView';
import NewForecast from './views/NewForecast';
import RangeSelect from './views/RangeSelect';
import StatusSelect from './views/StatusSelect';
import { ForecastDto } from 'utils/wemble-api.generated';
import { useCreateForecastMutation, useUpdateStatusMutation, useGetCurrentUserQuery, useUpdateForecastMutation } from 'utils/wemble-api';
import { getStatusSource } from 'utils/getStatusSource';
import { haptic } from 'utils/haptics';

import moment, { Moment } from 'moment-timezone';
moment.tz.setDefault("utc");

// Shared cooldown across all ForecastItem instances to prevent
// newly mounted items from immediately opening after a status selection
let globalCooldownUntil = 0;
const isTouch = 'ontouchstart' in window;
export const setGlobalForecastCooldown = (ms?: number) => { globalCooldownUntil = Date.now() + (ms ?? (isTouch ? 500 : 100)); };

const useStyles = makeStyles(() => ({
  forecastItem: {
    padding: 12,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'visible',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  },
  forecastItemDefault: {
    transition: 'transform 0.1s, opacity 0.1s',
    '&:active': {
      transform: 'scale(0.97)',
      opacity: 0.85,
    }
  },
  forecastItemLG: {
    minWidth: 160,
    maxWidth: 160,
  },
  forecastItemMD: {
    minWidth: 150,
    maxWidth: 150,
  },
  forecastItemSM: {
    minWidth: 145,
    maxWidth: 145,
  },
  forecastItemNew: {
    padding: 8,
    border: '8px dotted #F2F2F2',
    transition: 'transform 0.1s, opacity 0.1s',
    '& > *:not(:first-child)': {
      marginTop: '6px',
    },
    '&:active': {
      transform: 'scale(0.95)',
      opacity: 0.7,
    }
  },
  forecastItemNewHovered: {
    padding: 0,
    border: '0px dotted #F2F2F2',
    '& > *:not(:first-child)': {
      marginTop: '6px',
    }
  },
  addForecast: {
    marginTop: 8,
    fontSize: 15,
    fontWeight: 600,
    lineHeight: '120%',
    color: "#D2D2D2",
    textAlign: 'center'
  },
  suggestionItem: {
    padding: 3,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: '#F5F5F5',
    cursor: 'pointer',
    flexGrow: 1,
    transition: '0.1s',
  },
  title: {
    fontSize: 12,
    fontWeight: 600
  },
  subtitle: {
    fontSize: 9
  },
  commentInput: {
    fontSize: 11, fontWeight: 600, textAlign: 'center', padding: 0, width: 'calc(100% - 30px)', marginLeft: 2, marginRight: 4, background: 'none', border: 'none',
    '&:focus': {
      outline: 'none',
      background: '#FFFFFF77',
      borderRadius: 8
    }
  }
}));

const ForecastItem = ({
  today,
  nextDate,
  data,
  useSmallForecasts = false,
}: {
  today?: true;
  nextDate?: Moment;
  data?: ForecastDto;
  useSmallForecasts?: boolean;
}) => {

  const { data: user } = useGetCurrentUserQuery();

  const [createForecast] = useCreateForecastMutation();
  const [updateForecast] = useUpdateForecastMutation();

  const todayStatus = user?.onLeave ? 'away' : user?.currentWorkload?.toString()

  const [hovered, setHovered] = useState(false);
  const [isHoveringComment, setIsHoveringComment] = useState(false);
  const [range, setRange] = useState<{ startDate: string, endDate: string } | null>(null);
  const [status, setStatus] = useState<"0" | "1" | "2" | "3" | 'away' | null>(null);
  const [view, setView] = useState<'default' | 'new' | 'range' | 'custom-range' | 'status'>('new')
  const classes = useStyles();
  const cooldownRef = useRef(false);
  const autoClosedRef = useRef(false);

  const getSizeClass = () => {
    if (!useSmallForecasts) {
      return classes.forecastItemLG; // 160px when not using small forecasts
    }
    if (data?.comment) {
      return classes.forecastItemMD; // 150px when small forecasts but has comment
    }
    return classes.forecastItemSM; // 145px when small forecasts and no comment
  };

  const divRef = useRef<HTMLHeadingElement>(null);

  const views = {
    'default': () => (<DefaultView
      data={today ? { status: todayStatus } : data!}
      today={today}
      onCommentHoverStart={() => setIsHoveringComment(true)}
      onCommentHoverEnd={() => {
        setIsHoveringComment(false);
        if (!divRef.current?.matches(':hover')) {
          setHovered(false);
        }
      }}
    />),
    'new': () => (<NewForecast classes={classes} />),
    'range': () => (<RangeSelect classes={classes} selectRange={(setRange)} nextDate={nextDate} />),
    'status': () => (<StatusSelect classes={classes} selectStatus={setStatus} range={data ? data : range} data={data} toggle={() => setHovered(false)} />)
  }

  const [updateStatus] = useUpdateStatusMutation();

  const updateToday = () => {
    if (!user || !user._id || !status) return;

    updateStatus({
      type: 'current',
      statusUpdateParams: {
        status: status as '0' | '1' | '2' | '3' | 'away',
        source: getStatusSource()
      },
    });
  }

  useEffect(() => {
    if (data?.externalSource) setView('default');
    else if (!hovered) {
      if ((view === 'status' || view === 'range') && !autoClosedRef.current) haptic('selection');
      autoClosedRef.current = false;
      setRange(null)
      setStatus(null)
      setView((data || today) ? 'default' : 'new')
    }
    else if (typeof status == 'string') {
      if (data) updateForecast({ userId: user?._id || '', forecastId: data._id || '', forecastUpsertParams: { startDate: data.startDate!, endDate: data.endDate!, status, comment: data.comment, sessionId: (window as any)._sessionId, source: getStatusSource() } })
      else if (today) updateToday()
      else createForecast({ userId: user?._id || '', forecastUpsertParams: { startDate: range!.startDate, endDate: range!.endDate, status, sessionId: (window as any)._sessionId, source: getStatusSource() } })
      autoClosedRef.current = true;
      cooldownRef.current = true;
      globalCooldownUntil = Date.now() + (isTouch ? 500 : 100);
      setHovered(false)
      setTimeout(() => { cooldownRef.current = false; }, isTouch ? 500 : 100);
    }
    else if (range) { window.removeEventListener('mousemove', mouseListener.current); haptic('selection'); setView('status') }
    else if (view == 'new') { haptic('selection'); setView('range') }
    else if (view == 'default' && !isHoveringComment) { haptic('selection'); setView('status') }
  }, [hovered, range, status, isHoveringComment]);

  const mouseListener = useRef(() => {
    const h = divRef.current?.matches(':hover');
    if (!h) {
      setHovered(false);
      window.removeEventListener('mousemove', mouseListener.current);
    }
  });

  const mouseEnter = () => {
    if (cooldownRef.current || Date.now() < globalCooldownUntil) return;
    setHovered(true);
    window.removeEventListener('mousemove', mouseListener.current);
    window.addEventListener('mousemove', mouseListener.current);
  };

  // Close selector on any touch outside or any scroll/swipe gesture
  useEffect(() => {
    if (!hovered) return;
    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      cooldownRef.current = true;
      setHovered(false);
      setTimeout(() => { cooldownRef.current = false; }, isTouch ? 500 : 100);
    };
    let startY = 0;
    const onTouchStart = (e: TouchEvent) => {
      if (divRef.current && !divRef.current.contains(e.target as Node)) close();
      else startY = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (Math.abs(e.touches[0].clientY - startY) > 10) close();
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [hovered]);

  useEffect(() => {
    return () => {
      // Clean up the event listener on component unmount
      window.removeEventListener('mousemove', mouseListener.current);
    };
  }, []);

  return (
    <div
      ref={divRef}
      className={`${classes.forecastItem} ${getSizeClass()} ${view == 'default' ? classes.forecastItemDefault : (hovered ? classes.forecastItemNewHovered : classes.forecastItemNew)}`}
      style={{ backgroundColor: view == 'default' ? statusBackgroundColor(today ? todayStatus : data!.status) : '' }}
      onMouseEnter={mouseEnter}
      onClick={mouseEnter}
    >
      {views[view]()}
    </div>
  );
};

export default ForecastItem;

